import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import {
  createOuvidoria,
  getMotivosOuvidoria,
  getOuvidoriaById,
  getTagsOuvidoria,
  updateOuvidoria,
} from '@spark/services';
import {
  buildOption,
  buildOptionsFromItems,
  findOptionByValue,
  formatDateTime,
  getOptionBoolean,
  getOptionNumber,
  RELATADO_LIDER_OPTIONS,
  STATUS_ATUAL_OPTIONS,
  DESEJA_ACOMPANHAR_OPTIONS,
} from '../../../shared/constants';
import { formSections, getFormStructure, validateForm } from './formConfig';

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [motivoOptions, setMotivoOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;

  useEffect(() => {
    const loadBaseOptions = async () => {
      try {
        const [motivosResponse, tagsResponse] = await Promise.all([
          getMotivosOuvidoria({ pagina: 1, limitePorPagina: 200 }),
          getTagsOuvidoria({ pagina: 1, limitePorPagina: 200 }),
        ]);

        setMotivoOptions(buildOptionsFromItems(motivosResponse?.itens || [], 'id', 'nomeMotivo'));
        setTagOptions(buildOptionsFromItems(tagsResponse?.itens || [], 'id', 'nomeTag'));
      } catch (error) {
        console.error('Erro ao carregar opcoes da ouvidoria:', error);
        setMotivoOptions([]);
        setTagOptions([]);
      }
    };

    loadBaseOptions();
  }, []);

  useEffect(() => {
    if (!(isReadOnly || isEditMode) || !id || id === 'criar') {
      return;
    }

    const load = async () => {
      try {
        setLoadingData(true);
        const data = await getOuvidoriaById(id);
        const tagsDisplay = Array.isArray(data?.tags) && data.tags.length > 0
          ? data.tags
              .map(tagItem => tagItem?.tag?.nomeTag || `Tag ${tagItem?.tagId || '-'}`)
              .filter(Boolean)
              .join(', ')
          : '-';

        setInitialData({
          id: data?.id,
          motivoId: buildOption(data?.motivoId, data?.motivo?.nomeMotivo),
          relato: data?.relato || '',
          desejaAcompanhar: findOptionByValue(
            data?.desejaAcompanhar,
            DESEJA_ACOMPANHAR_OPTIONS
          ),
          relatadoLiderStatus: findOptionByValue(
            data?.relatadoLiderStatus,
            RELATADO_LIDER_OPTIONS
          ),
          statusAtual: findOptionByValue(data?.statusAtual, STATUS_ATUAL_OPTIONS),
          protocolo: data?.protocolo || '',
          telefone: data?.telefone || '',
          enderecoMotivo: data?.enderecoMotivo || '',
          dataHoraMotivo: formatDateTime(data?.dataHoraMotivo),
          filialNome: data?.filial?.nomeFilial || '',
          dataCriacao: formatDateTime(data?.dataCriacao),
          tagsDisplay,
          tags: Array.isArray(data?.tags)
            ? data.tags.map(tagItem =>
                buildOption(tagItem?.tagId, tagItem?.tag?.nomeTag || `Tag ${tagItem?.tagId}`)
              )
            : [],
        });
      } catch (error) {
        console.error('Erro ao carregar registro de ouvidoria:', error);
        notification.error('Nao foi possivel carregar o registro de ouvidoria.');
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [id, isEditMode, isReadOnly]);

  const defaultValues = useMemo(
    () => ({
      motivoId: null,
      relato: '',
      desejaAcompanhar: null,
      relatadoLiderStatus: null,
      statusAtual: null,
      protocolo: '',
      telefone: '',
      enderecoMotivo: '',
      dataHoraMotivo: '',
      filialNome: '',
      dataCriacao: '',
      tagsDisplay: '-',
      tags: [],
    }),
    []
  );

  const initialValues = initialData || defaultValues;

  const formStructure = useMemo(
    () =>
      getFormStructure({
        motivoOptions,
        tagOptions,
        disableTagsField: isEditMode || isReadOnly,
        isReadOnly,
      }),
    [isEditMode, isReadOnly, motivoOptions, tagOptions]
  );

  const onSubmit = useCallback(
    async values => {
      try {
        setLoading(true);

        const payloadBase = {
          motivoId: getOptionNumber(values.motivoId) || 0,
          relato: values.relato?.trim() || '',
          desejaAcompanhar: getOptionBoolean(values.desejaAcompanhar),
          relatadoLiderStatus: getOptionNumber(values.relatadoLiderStatus),
          statusAtual: getOptionNumber(values.statusAtual),
          protocolo: initialData?.protocolo || values.protocolo || '',
        };

        if (isEditMode) {
          await updateOuvidoria(id, payloadBase);
          notification.success('Registro de ouvidoria atualizado com sucesso!');
        } else {
          await createOuvidoria({
            ...payloadBase,
            tags: Array.isArray(values.tags)
              ? values.tags
                  .map(option => ({
                    tagId: getOptionNumber(option) || 0,
                  }))
                  .filter(tag => tag.tagId > 0)
              : [],
          });
          notification.success('Registro de ouvidoria criado com sucesso!');
        }

        history.push('/pages/ouvidoria');
      } catch (error) {
        console.error('Erro ao salvar registro de ouvidoria:', error);
        notification.error('Erro ao salvar o registro de ouvidoria. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [history, id, initialData?.protocolo, isEditMode]
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>
            Carregando dados do registro de ouvidoria...
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={formStructure}
                sections={formSections(initialValues)}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading ? 'true' : undefined}
                    disabled={loading}
                  >
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>

                  <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
